import {SectionBgView} from '../ui_canvas_engine/SectionBgView'
import {TextView} from '../ui_canvas_engine/TextView'
import {GameColor} from '../GameColor'
import {UIGameManager} from './UIGameManager'
import {GameStateEnums} from '../../../../shared/GameEnums'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {TextButtonView} from '../ui_canvas_engine/TextButtonView'
import {turnSecondsInToTimerFormat} from '../ui_utils/ClientUtils'
import {TimeUtils} from '../../../../shared/utils/TimeUtils'
import {PauseState} from '@colonist/game-state-types'
import {OpacityAnimationWrapper} from './ui_game_animations/OpacityAnimationWrapper'
import UIGameSettingsButton from './ui_game_help_buttons/UIGameSettingsButton'
import {KeyBindController, ShortcutLocation} from '../KeyBindController'
import {TranslationObject} from '@colonist/i18n'
import {VoteTypes} from '@colonist/game-base-types'
import {UIGameComponentZIndex} from './UIGameComponentZIndex'
import {UIGameMechanic} from './UIGameEventController'
import {TimeoutId} from '@colonist/types'

export abstract class UIGamePauseController implements UIGameMechanic {
    private readonly uiGameManager: UIGameManager
    private mainContainer: SectionBgView
    private contentContainer: SectionBgView
    private sectionSize: number
    private title: TextView
    private timerTextView: TextView
    private resumeButton: TextButtonView

    private readonly state: PauseState = {
        paused: false,
    }
    private interval?: TimeoutId | undefined

    protected BUTTON_CONTAINER_WIDTH_RATIO: number
    protected BUTTON_CONTAINER_HEIGHT_RATIO: number
    protected BUTTON_CONTAINER_Y_RATIO: number

    protected TITLE_FONT_SIZE: number
    protected TITLE_Y_PLACEMENT: number

    protected TIMER_FONT_SIZE: number
    protected TIMER_Y_PLACEMENT: number
    protected abstract initializePlacementRatios(): void

    constructor(uiGameManager: UIGameManager) {
        this.uiGameManager = uiGameManager
        this.uiGameManager.eventController.registerUIGameMechanic(this)
        this.initializePlacementRatios()

        this.drawPauseScreen()
        this.createKeyBind()
    }

    onEvent(data: any, eventType: GameStateEnums.UIGameEventEnums) {
        switch(eventType) {
            case GameStateEnums.UIGameEventEnums.CanvasResized: this.drawPauseScreen(); break
            default: break
        }
    }

    private drawPauseScreen() {
        const canvas = this.uiGameManager.canvasController
        this.mainContainer?.destroy()
        this.mainContainer = new SectionBgView(0, 0, canvas.appW - canvas.chatWidth, canvas.appH, canvas.app.stage, true)
        this.mainContainer.visible = false
        this.mainContainer.zIndex = UIGameComponentZIndex.Settings

        this.mainContainer.drawBg()
        this.contentContainer = new SectionBgView(0, 0, this.mainContainer.width, this.mainContainer.height, this.mainContainer)
        this.sectionSize = this.contentContainer.height / 6

        this.createTitle()
        this.createTimerText()
        if(!this.uiGameManager.gameController.isPlayerSpectator) this.createResumeButton()

        this.mainContainer.interactive = true
        this.animate()
        this.togglePause(this.state)
    }

    private get pauseTimerText(): TranslationObject {
        if(this.state.remainingPauseTime == undefined) return {key: 'strings:colonistCommon.utils.emptyString'}
        if(this.state.remainingPauseTime <= 0) return {key: 'strings:colonistCommon.utils.#buffer', options: {value: turnSecondsInToTimerFormat(0)}}
        return {key: 'strings:colonistCommon.utils.#buffer', options: {value: turnSecondsInToTimerFormat(Math.round(this.state.remainingPauseTime / TimeUtils.MILLISECONDS))}}
    }

    private createResumeButton() {
        const width = this.contentContainer.width * this.BUTTON_CONTAINER_WIDTH_RATIO
        const height = this.contentContainer.height * this.BUTTON_CONTAINER_HEIGHT_RATIO
        const x = this.contentContainer.width / 2 - width / 2
        const y = this.contentContainer.height * this.BUTTON_CONTAINER_Y_RATIO

        this.resumeButton = new TextButtonView(x, y, width, height, this.contentContainer)
        this.resumeButton.updateText({key: 'strings:game.pause.resume'})
        this.resumeButton.updateAction(() => {
            const isThereAVoteToTogglePause = this.uiGameManager.voteController.activeVote?.type == VoteTypes.TogglePause
            if(isThereAVoteToTogglePause) {
                this.uiGameManager.voteController.castVote(true)
                return
            }
            this.uiGameManager.socketGameSend.togglePause()
        })
    }

    private createTitle() {
        this.title = new TextView({key: 'strings:game.pause.paused'}, 0, this.sectionSize * this.TITLE_Y_PLACEMENT, this.contentContainer.width, this.sectionSize, this.contentContainer,
            this.TITLE_FONT_SIZE, GameColor.getColor(GameColor.Color.Gray3))
        this.title.centerXInParent()
    }

    private createTimerText() {
        const text = this.pauseTimerText
        this.timerTextView = new TextView(text, 0, this.sectionSize * this.TIMER_Y_PLACEMENT, this.contentContainer.width, this.sectionSize, this.contentContainer,
            this.TIMER_FONT_SIZE, GameColor.getColor(GameColor.Color.Gray3))
        this.timerTextView.centerX()
    }

    private updateTimerText() {
        this.timerTextView.setText(this.pauseTimerText)
        this.timerTextView.centerX()
        this.timerTextView.repositionY(true)
        // Mar 24, 2022 Omar: For some reason, without this, the text doesn't update
        this.timerTextView.tint = GameColor.getColorHexValue(GameColor.Color.Gray3)
    }

    private createKeyBind() {
        KeyBindController.bindKey('KeyP', () => this.onAutoPauseTriggered(), () => this.keyBindListener(), ShortcutLocation.InGame)
    }

    private keyBindListener() {
        if(this.state.paused) return false
        if(this.uiGameManager.settingsController.isActive() == true) return false
        return true
    }

    private animate() {
        new OpacityAnimationWrapper(this.contentContainer, this.uiGameManager, 0, 1, 700)
        new OpacityAnimationWrapper(this.title, this.uiGameManager, 0, 1, 300, 800)
        new OpacityAnimationWrapper(this.timerTextView, this.uiGameManager, 0, 1, 300, 800)
        if(this.resumeButton != undefined) new OpacityAnimationWrapper(this.resumeButton, this.uiGameManager, 0, 1, 300, 1000)
    }

    togglePause(state: PauseState) {
        this.state.remainingPauseTime = state.remainingPauseTime
        this.updateTimerText()

        this.state.paused = state.paused
        this.setVisible(this.state.paused)

        UIGameSettingsButton.updatePauseState(state.paused, this.uiGameManager.gameController.isPlayerSpectator)
        this.uiGameManager.eventController.sendEvent(this.state.paused, UIGameEventEnums.GamePauseToggled)
        this.updateTimer()
    }

    private setVisible(visible: boolean) {
        this.mainContainer.visible = visible
        this.animate()
    }

    isPaused(): boolean {
        return this.state.paused
    }

    private updateTimer() {
        const isPausedAndTimerNotStarted = this.state.paused && this.interval == undefined
        if(isPausedAndTimerNotStarted) {
            this.startTimer()
            return
        }

        const isNotPausedAndTimerRunning = !this.state.paused && this.interval != undefined
        if(isNotPausedAndTimerRunning) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            clearInterval(this.interval!)
            this.interval = undefined
        }
    }

    private startTimer() {
        if(this.state.remainingPauseTime == undefined) return
        let lastTime = Date.now()
        const timerPeriod = TimeUtils.MILLISECONDS
        this.interval = setInterval(() => {
            if(this.state.remainingPauseTime == undefined) return
            const timePassed = Date.now() - lastTime
            this.state.remainingPauseTime = this.state.remainingPauseTime - timePassed
            lastTime = Date.now()
            this.updateTimerText()
        }, timerPeriod)
    }

    onAutoPauseTriggered() {
        if(this.state.paused) return
        this.uiGameManager.socketGameSend.submitChat('/pause')
    }

    pauseIfSinglePlayer() {
        if(this.uiGameManager.gameController.isReplay) return
        if(this.state.paused) return
        if(!this.uiGameManager.gameState.hasOnlyOneHumanPlayer()) return
        this.uiGameManager.socketGameSend.togglePause()
    }
}
